import * as React from "react";
import { useDispatch, useSelector } from "react-redux";

import { ActivityActions, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { UaaActions, UaaSelectors, isUaaOidcUser } from "@com.mgmtp.a12.uaa/uaa-authentication-client";
import { LocalizerContext } from "@com.mgmtp.a12.utils/utils-localization-react/lib/main";
import { ButtonGroup } from "@com.mgmtp.a12.widgets/widgets-core/lib/button-group";
import { Button } from "@com.mgmtp.a12.widgets/widgets-core/lib/button/main/button.view";
import { HeaderTrigger } from "@com.mgmtp.a12.widgets/widgets-core/lib/button/main/header-trigger/header-trigger.view";
import { Icon } from "@com.mgmtp.a12.widgets/widgets-core/lib/icon";
import { SizeContext } from "@com.mgmtp.a12.widgets/widgets-core/lib/layout/size-detector";
import { List } from "@com.mgmtp.a12.widgets/widgets-core/lib/list/main/list.view";
import { ModalNotification } from "@com.mgmtp.a12.widgets/widgets-core/lib/modal-notification";
import { PopUpMenu } from "@com.mgmtp.a12.widgets/widgets-core/lib/pop-up-menu/main/pop-up-menu.view";

import { RESOURCE_KEYS } from "../localization";
import { isAdmin, isUser } from "../utilities/user";

const { Item } = List;

/**
 * Rendert das User-Menü oben rechts im Hauptmenü, mit Profilnamen, Logout-button, etc.
 */
export const UserInfoHeader: React.ComponentType = function UserInfoHeader() {
	const dispatch = useDispatch();

	const { localizer } = React.useContext(LocalizerContext);
	const { currentSize } = React.useContext(SizeContext);

	const user = useSelector(UaaSelectors.user);

	const closePopUpRef = React.useRef<() => void>(() => {});

	const onRoleClick = React.useCallback(() => {
		closePopUpRef.current();
		dispatch(ActivityActions.create({ activityDescriptor: { page: "roles" }, loadingState: "without" }));
	}, [dispatch, closePopUpRef]);

	const [logoutModalActive, setLogoutModalActive] = React.useState(false);

	const showLogoutModal = React.useCallback(() => {
		closePopUpRef.current();
		setLogoutModalActive(true);
	}, [setLogoutModalActive]);

	const hideLogoutModal = React.useCallback(() => {
		closePopUpRef.current();
		setLogoutModalActive(false);
	}, [setLogoutModalActive]);

	const admin = React.useMemo(() => (isUser(user) ? isAdmin(user) : false), [user]);

	if (user === undefined || !isUaaOidcUser(user)) {
		return <></>;
	}

	return (
		<>
			<PopUpMenu
				close={handler => (closePopUpRef.current = handler)}
				triggerElement={
					//A12-Widget hat ein Größenkonzept. Für die kleinen Größen sind xs und sm hier als "mobile" definiert.
					currentSize === "xs" || currentSize === "sm" ? (
						<HeaderTrigger graphic="account_circle" />
					) : (
						<HeaderTrigger
							graphic="account_circle"
							text={
								user.profile.given_name?.trim()?.slice(0, 1) + ". " + user.profile.family_name?.trim()
							}
							meta="arrow_drop_down"
						/>
					)
				}
			>
				<List>
					<Item
						text={user.profile.given_name?.trim() + " " + user.profile.family_name?.trim()}
						secondaryText="Angemeldet als"
						readonly
						flipped
						divider
					/>
					{admin ? (
						<Item text="Administrator:in" secondaryText="Rolle" readonly flipped divider />
					) : (
						<Item
							text={localizer({ key: RESOURCE_KEYS.application.view.roles.title })}
							onClick={onRoleClick}
							divider
						/>
					)}
					<Item
						text={
							<Button primary destructive onClick={showLogoutModal}>
								Abmelden
							</Button>
						}
						readonly
					/>
				</List>
			</PopUpMenu>
			{logoutModalActive && <LogoutModal onClose={hideLogoutModal} />}
		</>
	);
};

const LOGOUT_COMPONENT_RESSOURCE_KEYS = RESOURCE_KEYS.component.modal.logout;

const LogoutModal: React.ComponentType<{ onClose(): void }> = function LogoutModal({ onClose }) {
	const dispatch = useDispatch();
	const { localizer } = React.useContext(LocalizerContext);

	const onLogoutClick = React.useCallback(() => {
		window.onbeforeunload = () => {};
		dispatch(UaaActions.loggingOut());
	}, [dispatch]);

	const activities = useSelector(ActivitySelectors.allDirtyOrLockedActivities());

	return (
		<ModalNotification
			enableCloseButton
			onClose={onClose}
			variant="warning"
			title={localizer({ key: LOGOUT_COMPONENT_RESSOURCE_KEYS.title })}
			footer={
				<ButtonGroup alignment="right">
					<Button
						label={localizer({ key: LOGOUT_COMPONENT_RESSOURCE_KEYS.button.cancel.label })}
						icon={<Icon>close</Icon>}
						onClick={onClose}
					/>
					<Button
						label={localizer({ key: LOGOUT_COMPONENT_RESSOURCE_KEYS.button.logout.label })}
						icon={<Icon>logout</Icon>}
						onClick={onLogoutClick}
						primary
						destructive
					/>
				</ButtonGroup>
			}
		>
			<p>{localizer({ key: LOGOUT_COMPONENT_RESSOURCE_KEYS.message })}</p>
			{activities.length > 0 && (
				<p>
					<strong>{localizer({ key: RESOURCE_KEYS.general.indication.caution })}:</strong>{" "}
					{localizer({ key: LOGOUT_COMPONENT_RESSOURCE_KEYS.caution })}
				</p>
			)}
		</ModalNotification>
	);
};
